import React, { useEffect, useMemo, useState } from 'react';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
} from 'chart.js';
import type { ChartData, ChartOptions } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import microdebDashboardService, { MicrodebRevenueDashboardData } from '../../services/microdebDashboardService';
import './MicrodebRevenueDashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler
);

type ChartPalette = {
  text: string;
  muted: string;
  grid: string;
  accent: string;
  accentSoft: string;
  bar: string;
  tooltipBackground: string;
};

const DEFAULT_CHART_PALETTE: ChartPalette = {
  text: '#e5edf5',
  muted: '#94a3b8',
  grid: 'rgba(148, 163, 184, 0.18)',
  accent: '#4dd0e1',
  accentSoft: 'rgba(77, 208, 225, 0.18)',
  bar: 'rgba(77, 208, 225, 0.62)',
  tooltipBackground: 'rgba(15, 23, 42, 0.95)'
};

const formatCurrency = (value: number, currency = 'SEK') => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(value);
};

const formatPercent = (value: number | null) => {
  if (value === null) return 'N/A';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

const getMonthLabel = (month: string) => {
  if (/[A-Za-z]/.test(month)) {
    return month.replace(/\s+20(\d{2})$/, " '$1");
  }

  const parsed = new Date(`${month}-01T00:00:00`);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
  }
  return month;
};

interface StatCardProps {
  label: string;
  value: string;
  tone?: 'positive' | 'negative' | 'neutral';
}

type RevenueChartKey = 'momentum' | 'comparison';

const StatCard: React.FC<StatCardProps> = ({ label, value, tone = 'neutral' }) => (
  <div className={`microdeb-revenue-stat ${tone}`}>
    <span className="microdeb-revenue-stat-label">{label}</span>
    <strong className="microdeb-revenue-stat-value">{value}</strong>
  </div>
);

const MicrodebRevenueDashboard: React.FC = () => {
  const [data, setData] = useState<MicrodebRevenueDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedChart, setSelectedChart] = useState<RevenueChartKey>('momentum');
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadRevenueDashboard = async () => {
      try {
        setLoading(true);
        setError(null);
        const dashboardData = await microdebDashboardService.getRevenueDashboard();
        if (isMounted) {
          setData(dashboardData);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.response?.data?.error || err.message || 'Unable to load Nordrest revenue dashboard');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadRevenueDashboard();

    return () => {
      isMounted = false;
    };
  }, []);

  const latestMonths = useMemo(() => {
    return (data?.monthlyRevenue || []).slice(-12);
  }, [data?.monthlyRevenue]);

  const revenueLabels = useMemo(() => latestMonths.map(month => getMonthLabel(month.month)), [latestMonths]);
  const revenueValues = useMemo(() => latestMonths.map(month => month.revenue), [latestMonths]);
  const maxRevenue = useMemo(() => Math.max(...revenueValues, 0), [revenueValues]);

  const revenueLineData = useMemo<ChartData<'line', number[], string>>(() => ({
    labels: revenueLabels,
    datasets: [{
      label: 'Revenue',
      data: revenueValues,
      borderColor: DEFAULT_CHART_PALETTE.accent,
      backgroundColor: DEFAULT_CHART_PALETTE.accentSoft,
      borderWidth: 3,
      pointRadius: 3,
      pointHoverRadius: 6,
      pointBackgroundColor: DEFAULT_CHART_PALETTE.accent,
      pointBorderColor: DEFAULT_CHART_PALETTE.text,
      pointBorderWidth: 1.5,
      fill: true,
      tension: 0.36
    }]
  }), [revenueLabels, revenueValues]);

  const revenueBarData = useMemo<ChartData<'bar', number[], string>>(() => ({
    labels: revenueLabels,
    datasets: [{
      label: 'Monthly revenue',
      data: revenueValues,
      backgroundColor: DEFAULT_CHART_PALETTE.bar,
      borderColor: DEFAULT_CHART_PALETTE.accent,
      borderWidth: 1,
      borderRadius: 8,
      maxBarThickness: 34
    }]
  }), [revenueLabels, revenueValues]);

  const baseRevenueChartOptions = useMemo<ChartOptions<'line' | 'bar'>>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: DEFAULT_CHART_PALETTE.tooltipBackground,
        borderColor: DEFAULT_CHART_PALETTE.accent,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 10,
        titleColor: DEFAULT_CHART_PALETTE.text,
        bodyColor: DEFAULT_CHART_PALETTE.text,
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${formatCurrency(context.parsed.y, data?.currency)}`
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: DEFAULT_CHART_PALETTE.muted,
          font: {
            size: 11
          },
          maxRotation: 0
        }
      },
      y: {
        beginAtZero: true,
        suggestedMax: maxRevenue > 0 ? maxRevenue * 1.12 : undefined,
        grid: {
          color: DEFAULT_CHART_PALETTE.grid
        },
        ticks: {
          color: DEFAULT_CHART_PALETTE.muted,
          font: {
            size: 11
          },
          callback: (value: any) => formatCurrency(Number(value), data?.currency)
        }
      }
    }
  }), [data?.currency, maxRevenue]);

  const hasRevenueData = Boolean(data && latestMonths.length > 0);
  const momTone = data?.momChangePercent === null || data?.momChangePercent === undefined
    ? 'neutral'
    : data.momChangePercent >= 0 ? 'positive' : 'negative';
  const selectedChartTitle = selectedChart === 'momentum' ? 'Revenue Momentum' : 'Monthly Comparison';

  return (
    <section
      className={`microdeb-revenue-dashboard ${isMinimized ? 'minimized' : ''}`}
      aria-labelledby="microdeb-revenue-title"
    >
      <div className="microdeb-revenue-header">
        <div>
          <span className="microdeb-revenue-eyebrow">Microdeb</span>
          <h2 id="microdeb-revenue-title">Nordrest Revenue Dashboard</h2>
        </div>
        <div className="microdeb-revenue-header-actions">
          {loading && !isMinimized && <span className="microdeb-revenue-status">Loading revenue...</span>}
          <button
            type="button"
            className="microdeb-revenue-toggle"
            onClick={() => setIsMinimized(current => !current)}
            aria-expanded={!isMinimized}
            aria-controls="microdeb-revenue-dashboard-body"
          >
            {isMinimized ? 'Maximize' : 'Minimize'}
          </button>
        </div>
      </div>

      {!isMinimized && (
        <div id="microdeb-revenue-dashboard-body">
      {error ? (
        <div className="microdeb-revenue-state error" role="status">
          {error}
        </div>
      ) : !loading && !hasRevenueData ? (
        <div className="microdeb-revenue-state" role="status">
          No Nordrest revenue data is available yet.
        </div>
      ) : (
        <>
          <div className="microdeb-revenue-stats">
            <StatCard label="Total revenue" value={formatCurrency(data?.totalRevenue || 0, data?.currency)} />
            <StatCard label="Latest month" value={formatCurrency(data?.latestMonthRevenue || 0, data?.currency)} />
            <StatCard label="Monthly average" value={formatCurrency(data?.averageMonthlyRevenue || 0, data?.currency)} />
            <StatCard label="MoM change" value={formatPercent(data?.momChangePercent ?? null)} tone={momTone} />
          </div>

          {hasRevenueData && (
            <div className="microdeb-revenue-chart-card">
              <div className="microdeb-revenue-chart-header">
                <span>12-month trend</span>
                <span>Interactive revenue view</span>
              </div>

              <div className="microdeb-revenue-chart-tabs" role="tablist" aria-label="Revenue chart view">
                <button
                  type="button"
                  className={`microdeb-revenue-chart-tab ${selectedChart === 'momentum' ? 'active' : ''}`}
                  role="tab"
                  aria-selected={selectedChart === 'momentum'}
                  aria-controls="microdeb-revenue-chart-panel"
                  id="microdeb-revenue-momentum-tab"
                  onClick={() => setSelectedChart('momentum')}
                >
                  Revenue Momentum
                </button>
                <button
                  type="button"
                  className={`microdeb-revenue-chart-tab ${selectedChart === 'comparison' ? 'active' : ''}`}
                  role="tab"
                  aria-selected={selectedChart === 'comparison'}
                  aria-controls="microdeb-revenue-chart-panel"
                  id="microdeb-revenue-comparison-tab"
                  onClick={() => setSelectedChart('comparison')}
                >
                  Monthly Comparison
                </button>
              </div>

              <div
                className="microdeb-revenue-chart-panel"
                role="tabpanel"
                id="microdeb-revenue-chart-panel"
                aria-labelledby={`microdeb-revenue-${selectedChart === 'momentum' ? 'momentum' : 'comparison'}-tab`}
              >
                <div className="microdeb-revenue-panel-header">
                  <span>{selectedChartTitle}</span>
                  <strong>
                    {selectedChart === 'momentum'
                      ? formatCurrency(latestMonths[latestMonths.length - 1]?.revenue || 0, data?.currency)
                      : `${latestMonths.length} months`}
                  </strong>
                </div>
                <div
                  className="microdeb-revenue-chart-container"
                  role="img"
                  aria-label={selectedChart === 'momentum'
                    ? '12 month Nordrest revenue line chart'
                    : 'Nordrest monthly revenue bar chart'}
                >
                  {selectedChart === 'momentum' ? (
                    <Line
                      data={revenueLineData}
                      options={baseRevenueChartOptions as ChartOptions<'line'>}
                    />
                  ) : (
                    <Bar
                      data={revenueBarData}
                      options={baseRevenueChartOptions as ChartOptions<'bar'>}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
        </div>
      )}
    </section>
  );
};

export default MicrodebRevenueDashboard;
